import { useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useScrollSync, useSnackbar } from '@/hooks';
import { UserManagement } from '@/types/user-management.types';
import {
  useDeleteUserMutation,
  useGetUsersListQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateSalesExpertsExclusionsMutation
} from '@/services';

import {
  PageHeader,
  UserForm,
  SearchSection,
  SalesExpertsTab,
  DeleteDialog,
  UserFormData,
  TableHeader,
  UsersTab
} from './components';

import './UserManagementPage.css';
import {
  roleLabels,
  getCreateOrUpdateErrorMessage,
  getDeleteUserErrorMesage,
  getUpdateData,
  getCreateData
} from './user-management.utils';

function UserManagementPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setSnackbarMessage } = useSnackbar();
  const { headerRef: usersHeaderRef, bodyRef: usersBodyRef } = useScrollSync();
  const { headerRef: marketersHeaderRef, bodyRef: marketersBodyRef } = useScrollSync();

  const { isLoading: isUsersLoading } = useGetUsersListQuery(undefined, { refetchOnMountOrArgChange: true });

  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [updateSalesExpertsExclusions] = useUpdateSalesExpertsExclusionsMutation();

  const [isSavingExclusions, setIsSavingExclusions] = useState(false);

  const [exclusionUpdatesCount, setExclusionUpdatesCount] = useState(0);
  const [pendingExclusions, setPendingExclusions] = useState<import('@/types/user-management.types').ExclusionUpdate[]>(
    []
  );
  const [resetRequestId, setResetRequestId] = useState(0);
  const [activeTab, setActiveTab] = useState<'users' | 'marketers'>('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [userToDelete, setUserToDelete] = useState<UserManagement | null>(null);
  const [editingUser, setEditingUser] = useState<UserManagement | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleSaveExclusionsFromTab = async () => {
    if (!pendingExclusions?.length) return;
    try {
      setIsSavingExclusions(true);
      await updateSalesExpertsExclusions({ exclusions: pendingExclusions }).unwrap();
      setSnackbarMessage('Налаштування активності оновлено успішно', 'success');
      setPendingExclusions([]);
      setExclusionUpdatesCount(0);
      setResetRequestId(prev => prev + 1);
    } catch (err) {
      setSnackbarMessage('Помилка оновлення налаштувань активності', 'error');
    } finally {
      setIsSavingExclusions(false);
    }
  };

  const handleResetExclusionsFromTab = () => {
    setResetRequestId(prev => prev + 1);
    setPendingExclusions([]);
    setExclusionUpdatesCount(0);
  };

  const handleSort = (column: string) => {
    let newSortOrder: 'ASC' | 'DESC';
    if (sortBy === column) {
      newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      newSortOrder = 'ASC';
    }

    setSortBy(column);
    setSortOrder(newSortOrder);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleTabChange = (tab: 'users' | 'marketers') => {
    setActiveTab(tab);
  };
  const showCreateForm = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleEditIconClick = (user: UserManagement) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDeleteIconClick = (user: UserManagement) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleSubmit = async (data: UserFormData) => {
    try {
      if (editingUser) {
        const updateData = getUpdateData(data);
        await updateUser({ id: editingUser.id, data: updateData }).unwrap();

        setSnackbarMessage('Користувача успішно оновлено', 'success');
      } else {
        const createData = getCreateData(data);
        await createUser(createData).unwrap();
        setSnackbarMessage('Користувача успішно створено', 'success');
      }
      cancelForm();
    } catch (error: unknown) {
      const err = error as any;
      const errorMsg = getCreateOrUpdateErrorMessage(err, editingUser);
      setSnackbarMessage(errorMsg, 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id).unwrap();
      setSnackbarMessage('Користувача успішно видалено', 'success');
    } catch (error: unknown) {
      const err = error as any;
      const errorMsg = getDeleteUserErrorMesage(err);
      setSnackbarMessage(errorMsg, 'error');
    } finally {
      setOpenDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  return (
    <>
      <Box className="user-management-container">
        <Box className="user-management-sticky-header">
          <PageHeader
            activeTab={activeTab}
            dataInitialized={!isUsersLoading}
            exclusionUpdatesCount={exclusionUpdatesCount}
            isMobile={isMobile}
            isEditing={showForm}
            onTabChange={handleTabChange}
            onAddUser={showCreateForm}
            onSaveExclusions={handleSaveExclusionsFromTab}
            onResetExclusions={handleResetExclusionsFromTab}
            isSaving={isSavingExclusions}
          />

          <div className="user-management-form-section">
            {showForm && (
              <UserForm
                editingUser={editingUser}
                isLoading={isCreating || isUpdating}
                onSubmit={handleSubmit}
                onCancel={cancelForm}
              />
            )}

            <SearchSection value={searchQuery} onChange={handleSearchChange} />
          </div>

          <TableHeader
            activeTab={activeTab}
            usersHeaderRef={usersHeaderRef}
            marketersHeaderRef={marketersHeaderRef}
            onSort={handleSort}
          />
        </Box>

        <div className="user-management-results-container">
          <UsersTab
            activeTab={activeTab}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            isDeleting={isDeleting}
            usersBodyRef={usersBodyRef}
            roleLabels={roleLabels}
            handleEditIconClick={handleEditIconClick}
            handleDeleteIconClick={handleDeleteIconClick}
          />
        </div>

        <SalesExpertsTab
          activeTab={activeTab}
          searchQuery={searchQuery}
          onExclusionUpdatesChange={updates => {
            setPendingExclusions(updates);
            setExclusionUpdatesCount(updates?.length || 0);
          }}
          bodyRef={marketersBodyRef}
          resetRequestId={resetRequestId}
        />

        <DeleteDialog
          openDeleteDialog={openDeleteDialog}
          isDeleting={isDeleting}
          isMobile={isMobile}
          userToDelete={userToDelete}
          handleConfirmDelete={handleConfirmDelete}
          setOpenDeleteDialog={setOpenDeleteDialog}
        />
      </Box>
    </>
  );
}

export default UserManagementPage;
